// v2.71.77 — Global runtime error guards (outside React's lifecycle).
//
// React's ErrorBoundary catches RENDER errors. It does NOT catch:
//   • Errors thrown inside event handlers (those propagate to window.onerror)
//   • Unhandled promise rejections (those propagate to unhandledrejection)
//   • Async errors fired after a component has unmounted
//
// This module installs two listeners that:
//   1. Log the error with a clear prefix so it's findable in production
//      console traces.
//   2. Swallow noisy network errors that are EXPECTED (cancelled requests,
//      AbortController-triggered cancellations) so they don't pollute logs.
//   3. Forward chunk-load failures to a single forced reload (mirrors the
//      ErrorBoundary handling for stale deploys).
//
// Install once from src/index.js — runs in module side-effect.

function isChunkLoadError(err) {
  if (!err) return false;
  const name = String(err.name || "");
  const msg = String(err.message || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

function isExpectedNoise(err) {
  if (!err) return false;
  const name = String(err.name || "");
  const msg = String(err.message || "");
  // Axios cancellation (AbortController.abort()) and fetch aborts.
  if (name === "CanceledError" || name === "AbortError") return true;
  if (/canceled|aborted/i.test(msg)) return true;
  // ResizeObserver loop warnings — benign, fire when an observed element
  // changes size during the observer callback. Browsers throw these
  // sometimes during normal layout work. Common false positive.
  if (/ResizeObserver loop/.test(msg)) return true;
  return false;
}

let installed = false;

export function installGlobalErrorGuards() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const err = event.error || event.reason || new Error(event.message);
    if (isExpectedNoise(err)) {
      event.preventDefault?.();
      return;
    }
    if (isChunkLoadError(err)) {
      try {
        const k = "he-reloaded-for-chunk-window";
        if (!sessionStorage.getItem(k)) {
          sessionStorage.setItem(k, "1");
          window.location.reload();
        }
      } catch (_) { /* noop */ }
      return;
    }
    // eslint-disable-next-line no-console
    console.error("[window.onerror]", err);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const err = event.reason;
    if (isExpectedNoise(err)) {
      event.preventDefault?.();
      return;
    }
    if (isChunkLoadError(err)) {
      try {
        const k = "he-reloaded-for-chunk-promise";
        if (!sessionStorage.getItem(k)) {
          sessionStorage.setItem(k, "1");
          window.location.reload();
        }
      } catch (_) { /* noop */ }
      return;
    }
    // eslint-disable-next-line no-console
    console.error("[unhandledrejection]", err);
  });
}
